/**
 * A document can say it has moved, and every repo follows it (RFC 0346 3f).
 *
 * WHY A DOCUMENT MOVES. History cannot be cut out of an Automerge document in
 * place: every peer that holds the old changes merges them back. The project
 * document held 0.8 MB of state behind 2.97 million ops of history, loaded in
 * full by the daemon, the window's worker and its tab. The way out is a new
 * document made from the current state, with a new id. The old one keeps a
 * single field, `movedTo`, naming the new id.
 *
 * WHY HERE. Ids are stored in many places: the World's project list, every
 * session and document, the desktop's launch argument, mounts, spaces, URLs.
 * Moving each one is the migration; following the pointer is what keeps a
 * place that was missed, or a client that read an old id, landing on the right
 * document. Every client builds its repo with `createModRepo`, and every read
 * goes through `find` or `findWithProgress`, so following here covers all of
 * them without any caller learning about moves.
 *
 * BOUNDED. At most `MAX_HOPS` moves are followed, and a loop stops where it
 * started, so a bad pointer can cost a read but never hang one.
 *
 * A KNOWN MOVE IS NOT LOADED. Following `movedTo` means loading the old
 * document first, and the old project is 3 million ops: on Oct 12 the daemon
 * and the window's worker each still held it, a gigabyte of wasm that never
 * comes back. So each repo remembers the moves it has seen, from a followed
 * `movedTo` or from any document read that carries a `moves` map (the World
 * does), and a read of a known old id goes straight to the new one.
 */

import type {
  AnyDocumentId,
  DocHandle,
  Repo,
} from '@automerge/automerge-repo/slim'

/** The field a moved document keeps, naming where it went. */
export const MOVED_TO = 'movedTo'

/** A move of a move is followed; a longer chain is a mistake, not a plan. */
export const MAX_HOPS = 3

/** Where `doc` says it moved to, if it does. */
export function movedTo(doc: unknown, from: string): string | null {
  const to = (doc as Record<string, unknown> | null | undefined)?.[MOVED_TO]
  const id = to === undefined || to === null ? '' : String(to)
  return id.length > 0 && id !== from ? id : null
}

type QueryState<T> =
  | { state: 'ready'; handle: DocHandle<T> }
  | { state: string; handle?: DocHandle<T>; [k: string]: unknown }

interface Progress<T> {
  readonly documentId: string
  peek(): QueryState<T>
  subscribe(cb: (state: QueryState<T>) => void): () => void
  whenReady(options?: { signal?: AbortSignal }): Promise<DocHandle<T>>
}

const FOLLOWED = Symbol.for('mod.followMoves')

/** Each repo's known moves, old id to new. */
const known = new WeakMap<object, Map<string, string>>()

/**
 * Where a repo keeps its known moves between runs. A window read the old
 * project id from a session before it had read the World, and loaded the old
 * 3 million ops again on every reload (Oct 12).
 */
export interface MoveMemory {
  read(): unknown
  write(moves: Record<string, string>): void
}

const memories = new WeakMap<object, MoveMemory>()

function remember(repo: Repo): void {
  const map = known.get(repo)
  const memory = memories.get(repo)
  if (!map || !memory) return
  try {
    memory.write(Object.fromEntries(map))
  } catch {
    // A memory that cannot be written costs a load, never a read.
  }
}

/** Set a move, and keep it if it is new. */
function setMove(repo: Repo, from: string, to: string): void {
  const map = known.get(repo)
  if (!map || map.get(from) === to) return
  map.set(from, to)
  remember(repo)
}

const bare = (id: unknown): string => String(id).replace(/^automerge:/, '')

/** Tell `repo` about moves it should follow without loading the old id. */
export function noteMoves(repo: Repo, moves: unknown): void {
  const map = known.get(repo)
  if (!map || !moves || typeof moves !== 'object') return
  for (const [from, to] of Object.entries(moves as Record<string, unknown>)) {
    const id = to === undefined || to === null ? '' : bare(to)
    if (id && id !== from) setMove(repo, from, id)
  }
}

/** Forget a move, because it was undone. */
export function forgetMove(repo: Repo, from: string): void {
  if (known.get(repo)?.delete(bare(from))) remember(repo)
}

/**
 * Where a read of `id` should go, by the moves this repo knows. When the old
 * document is loaded anyway, its own `movedTo` is the truth: a move undone
 * on another device is not followed from memory.
 */
function redirect(
  repo: Repo,
  map: Map<string, string>,
  id: AnyDocumentId,
): AnyDocumentId {
  let at = bare(id)
  if (!map.has(at)) return id
  const handles = repo.handles as Record<string, DocHandle<unknown>>
  const seen = new Set([at])
  for (let hop = 0; hop < MAX_HOPS; hop++) {
    const held = handles[at]
    if (held?.isReady() && !movedTo(held.doc(), at)) {
      forgetMove(repo, at)
      break
    }
    const to = map.get(at)
    if (!to || seen.has(to)) break
    seen.add(to)
    at = to
  }
  return at === bare(id) ? id : (at as AnyDocumentId)
}

/** Learn what a document read says about moves: its own, and a `moves` map. */
function learn(repo: Repo, handle: DocHandle<unknown> | undefined): void {
  if (!handle?.isReady()) return
  const doc = handle.doc() as Record<string, unknown> | undefined
  if (!doc) return
  const from = String(handle.documentId)
  const to = movedTo(doc, from)
  if (to) setMove(repo, from, to)
  if (doc.moves) noteMoves(repo, doc.moves)
}

/** Make `repo`'s `find` and `findWithProgress` follow moved documents. */
export function followMoves(repo: Repo, memory?: MoveMemory): Repo {
  const r = repo as unknown as Record<string | symbol, unknown>
  if (r[FOLLOWED]) return repo
  r[FOLLOWED] = true
  const map = new Map<string, string>()
  known.set(repo, map)
  if (memory) {
    try {
      noteMoves(repo, memory.read())
    } catch {
      // Nothing remembered is the same as a first run.
    }
    memories.set(repo, memory)
  }

  const find = (r.find as Repo['find']).bind(repo)
  r.find = async (id: AnyDocumentId, ...rest: unknown[]) => {
    const seen = new Set<string>()
    let handle = await (
      find as (...a: unknown[]) => Promise<DocHandle<unknown>>
    )(redirect(repo, map, id), ...rest)
    learn(repo, handle)
    for (let hop = 0; hop < MAX_HOPS; hop++) {
      const from = String(handle.documentId)
      seen.add(from)
      const to = handle.isReady() ? movedTo(handle.doc(), from) : null
      if (!to || seen.has(to)) break
      handle = await (find as (...a: unknown[]) => Promise<DocHandle<unknown>>)(
        to as AnyDocumentId,
        ...rest,
      )
      learn(repo, handle)
    }
    return handle
  }

  const findWithProgress = r.findWithProgress as
    | ((id: AnyDocumentId, options?: unknown) => Progress<unknown>)
    | undefined
  if (typeof findWithProgress === 'function') {
    const open = findWithProgress.bind(repo)
    r.findWithProgress = (id: AnyDocumentId, options?: unknown) =>
      following(
        open(redirect(repo, map, id), options),
        (to) => open(to as AnyDocumentId, options),
        (h) => learn(repo, h),
      )
  }
  return repo
}

/**
 * A progress that becomes the moved-to document's progress once the first one
 * is ready and says it moved. Until then it is the first one, unchanged.
 */
function following<T>(
  first: Progress<T>,
  open: (id: string) => Progress<T>,
  onReady: (handle: DocHandle<T>) => void,
  hops = 0,
  seen: Set<string> = new Set([String(first.documentId)]),
): Progress<T> {
  let next: Progress<T> | null = null
  let learned = false
  const resolve = (): Progress<T> => {
    if (next) return next
    const s = first.peek()
    if (s.state !== 'ready' || !s.handle) return first
    if (!learned) {
      learned = true
      onReady(s.handle)
    }
    if (hops >= MAX_HOPS) return first
    const to = movedTo(s.handle.doc(), String(first.documentId))
    if (!to || seen.has(to)) return first
    seen.add(to)
    next = following(open(to), open, onReady, hops + 1, seen)
    return next
  }
  return {
    get documentId() {
      return resolve().documentId
    },
    peek: () => resolve().peek(),
    subscribe(cb) {
      let inner: (() => void) | null = null
      let current: Progress<T> | null = null
      const attach = () => {
        const target = resolve()
        if (target === current) return
        inner?.()
        current = target
        inner = target === first ? null : target.subscribe((state) => cb(state))
      }
      const outer = first.subscribe((state) => {
        attach()
        watchMove()
        cb(current && current !== first ? current.peek() : state)
      })
      // A document that moves WHILE it is open (a session page closing, RFC
      // 0360) changes without its query's state changing: watch the ready
      // handle for `movedTo` and hand the subscriber the next page's query.
      let moveWatch: (() => void) | null = null
      const watchMove = () => {
        if (moveWatch) return
        const s = first.peek()
        if (s.state !== 'ready' || !s.handle) return
        const h = s.handle as DocHandle<T> & {
          on?: (ev: 'change', f: () => void) => void
          off?: (ev: 'change', f: () => void) => void
        }
        if (!h.on || !h.off) return
        const onChange = () => {
          if (!movedTo(h.doc(), String(first.documentId))) return
          attach()
          if (current && current !== first) cb(current.peek())
        }
        h.on('change', onChange)
        moveWatch = () => h.off?.('change', onChange)
      }
      attach()
      watchMove()
      return () => {
        outer()
        inner?.()
        moveWatch?.()
      }
    },
    async whenReady(options) {
      await first.whenReady(options)
      const target = resolve()
      return target === first
        ? first.whenReady(options)
        : target.whenReady(options)
    },
  }
}
